/**
 * The account selector's state, as ONE decision made in ONE place.
 *
 * THE DEFECT THIS EXISTS FOR
 * --------------------------
 * *"When loading accounts in editing a job or creating a new one the drop downs take a long
 * time to render. In the case of an edit, a guid is displayed for 5-9 seconds until it is
 * replaced by the proper populated list. […] It 'reads' broken at first, but then populates and
 * works 5-10 seconds later."*
 *
 * The editor held ONE boolean about the account list — `providerAccountsError` — so it could say
 * "loaded" or "failed" and nothing else. "Has not arrived yet" therefore rendered as
 * "loaded, and empty", which produced two false statements at once on every edit page:
 *
 *   * the stored account reference matched no option, so the fallback option meant for a
 *     genuinely DANGLING reference fired and put a bare UUID on screen as the selected value; and
 *   * on a create page the "no account of this kind exists yet — go make one" panel appeared,
 *     which is a claim about the operator's configuration made while nothing had been read.
 *
 * Both are the app's signature failure: a surface misdescribing a working engine.
 *
 * WHY A SEPARATE MODULE
 * ---------------------
 * No React, no imports: `tests/test_account_selector_states.py` transpiles this with esbuild and
 * EXECUTES it under node, so the states are proven by behaviour rather than by grepping JSX.
 * The same reason `identityEnforcement.ts` sits beside `IdentitySettings.tsx`.
 *
 * THE RULE, and it is the only rule: **"not yet arrived", "arrived and this account is gone",
 * and "could not be read" are THREE states and must never be collapsed.** The first is transient
 * and needs nothing from the operator; the second needs them to repair the job; the third needs
 * them to reload and forbids saving over a reference they cannot currently see.
 */

/** What the account fetch has actually told us. `'loading'` is a real answer, not an absence. */
export type AccountListStatus = 'loading' | 'loaded' | 'failed'

/**
 * What the selector should present.
 *
 * A discriminated union rather than a bag of booleans: the four cases are mutually exclusive and
 * a union makes rendering two of them at once — which is what the single boolean allowed — a type
 * error rather than a judgement call at each use site.
 */
export type AccountSelectorState =
  /** The list has not arrived. Say so; claim nothing about what it will contain. */
  | { kind: 'loading' }
  /** The list could not be read. A DISPLAY failure, disclosed on purpose. */
  | { kind: 'failed'; storedId: string }
  /** The list arrived and holds no account of this kind, and this leg names none. */
  | { kind: 'none-exist' }
  /** The list arrived and does NOT contain the account this leg names — the job needs repair. */
  | { kind: 'dangling'; storedId: string }
  /** The list arrived and can be chosen from. */
  | { kind: 'ready' }

/**
 * The selector's state for one leg.
 *
 * @param status     what the fetch has reported
 * @param accountIds the ids of the accounts of THIS leg's kind, disabled ones included
 * @param storedId   the account id this leg currently names; `''` when unset
 *
 * Ordering matters and is deliberate:
 *
 * 1. `loading` wins over everything. While the list is unknown, `accountIds` is empty for a
 *    reason that has nothing to do with the operator's configuration, so every verdict derived
 *    from its emptiness — "none exist", "your reference is gone" — would be a claim about data
 *    that has not been read. This is the ordering the boolean could not express.
 * 2. `failed` likewise: an empty list from a failed fetch is not an empty configuration.
 * 3. `dangling` outranks `none-exist`. A leg pointing at an account that is gone needs REPAIR,
 *    and "no account exists yet, go and create one" is the wrong instruction for it even though
 *    both happen to see zero selectable options.
 */
export function accountSelectorState(
  status: AccountListStatus,
  accountIds: readonly string[],
  storedId: string,
): AccountSelectorState {
  if (status === 'loading') return { kind: 'loading' }
  if (status === 'failed') return { kind: 'failed', storedId }
  if (storedId && !accountIds.includes(storedId)) return { kind: 'dangling', storedId }
  if (accountIds.length === 0) return { kind: 'none-exist' }
  return { kind: 'ready' }
}

/**
 * The value the `<select>` must be given.
 *
 * `''` while loading or after a failure, which is THE fix for the reported symptom: a controlled
 * select whose value is an id with no matching option renders that id, and the editor's fallback
 * option rendered it as the visible label. Returning `''` means the only thing on screen is the
 * explicit "Loading accounts…" / "unavailable" option.
 *
 * This does NOT clear the reference. The form's own `outbound_account_id` / `inbound_account_id`
 * is untouched — nothing but the operator's `onChange` writes it — so a save while the list is
 * still in flight stores exactly what was stored before. That distinction is the whole reason
 * this returns a RENDERING value rather than mutating form state.
 */
export function accountSelectorValue(
  state: AccountSelectorState,
  storedId: string,
): string {
  return state.kind === 'loading' || state.kind === 'failed' ? '' : storedId
}
